// @flow
import React, { Component } from 'react';
import isFunction from 'lodash/isFunction';
import type { Experience as ExperienceData } from 'app/common/lib/react/experiences/experienceUtils';
import type { ExperienceClient } from 'app/common/lib/react/experiences/withExperienceClient';
import withExperienceClient from 'app/common/lib/react/experiences/withExperienceClient';

const UnavailableExperience = () => null;

type ExperienceProps = {|
  children?: any,
  disableAutoView?: boolean,
  experienceClient: ExperienceClient,
  experienceIds?: Array<number>,
  preventRemoval?: boolean,
  // Temporary prop used in order to transition an existing experience in EHQ from
  // one type to another.
  // TODO(juliac): remove after transition from NAG_WEB to BANNER_WEB is complete
  transitionType?: number,
  type?: number,
  completeButtonUrl?: string,
|};

/*
 * Represents a single experience. The experience will be automatically viewed
 * and this component will render nothing if the given experience does not exist.
 *
 * WARNING: To use this component, default import should be used so that
 * experienceClient prop can be received from withExperienceClient HOC.
 */
// $FlowFixMe Flow v0.104.0 upgrade supression.
export class Experience extends Component<ExperienceProps, *> {
  componentDidMount() {
    if (this.props.disableAutoView) {
      return;
    }
    const experience = this.getExperience(this.props);
    if (experience && this.shouldRenderExperience(experience)) {
      this.view(experience);
    }
  }

  // $FlowFixMe Flow v0.104.0 upgrade supression.
  componentDidUpdate(prevProps: ExperienceProps, prevState: *) {
    if (this.props.disableAutoView) {
      return;
    }
    const oldExperience = this.getExperience(prevProps);
    const newExperience = this.getExperience(this.props);
    if (!newExperience || !this.shouldRenderExperience(newExperience)) {
      return;
    }
    if (!oldExperience || oldExperience.experience_id !== newExperience.experience_id) {
      this.view(newExperience);
    }
  }

  getExperience(props: ExperienceProps): ?ExperienceData {
    return props.experienceClient.experienceForPlacement;
  }

  shouldRenderExperience(experience: ?ExperienceData) {
    if (experience) {
      const { type, experience_id } = experience;
      if (this.props.experienceIds && this.props.experienceIds.includes(experience_id)) {
        return true;
      }
      if (!this.props.experienceIds) {
        if (!!this.props.type && this.props.type === type) {
          return true;
        }
        if (!!this.props.transitionType && this.props.transitionType === type) {
          return true;
        }
      }
    }
    return false;
  }

  view = (experience: ExperienceData) => {
    const { placement_id, experience_id } = experience;
    const { experienceClient } = this.props;
    experienceClient.viewExperience(placement_id, experience_id);
  };

  complete = (experience: ExperienceData) => {
    const { placement_id, experience_id } = experience;
    const { experienceClient, preventRemoval } = this.props;
    if (preventRemoval) {
      experienceClient.completeExperience(placement_id, experience_id, preventRemoval);
    } else {
      experienceClient.completeExperience(placement_id, experience_id);
    }
  };

  dismiss = (experience: ExperienceData) => {
    const { placement_id, experience_id } = experience;
    const { experienceClient, preventRemoval } = this.props;
    if (preventRemoval) {
      experienceClient.dismissExperience(placement_id, experience_id, preventRemoval);
    } else {
      experienceClient.dismissExperience(placement_id, experience_id);
    }
  };

  render() {
    const { children, disableAutoView } = this.props;
    const experience = this.getExperience(this.props);
    if (experience && this.shouldRenderExperience(experience) && children) {
      if (isFunction(children)) {
        const childrenProps = {
          ...experience,
          complete: () => this.complete(experience),
          dismiss: () => this.dismiss(experience),
        };
        if (disableAutoView) {
          // $FlowFixMe: new errors found from flow 0.71 upgrade
          childrenProps.view = () => this.view(experience);
        }
        return children(childrenProps);
      } else {
        return React.Children.only(children);
      }
    } else {
      return <UnavailableExperience />;
    }
  }
}

export default withExperienceClient(Experience);
